import { useQuery } from "@tanstack/react-query";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, PRODUCT_LABEL, SOURCE_LABEL, STAGES } from "../lib/api";

const STAGE_LABEL: Record<string, string> = Object.fromEntries(STAGES.map((s) => [s.key, s.title]));
const STAGE_COLOR: Record<string, string> = {
  new: "#5b8def",
  in_work: "#c084fc",
  negotiation: "#ffb454",
  invoice_sent: "#fde047",
  paid: "#6dd6a6",
  closed_won: "#34d399",
  closed_lost: "#ff6e6e",
};

export function Analytics() {
  const funnelQ = useQuery({ queryKey: ["funnel"], queryFn: api.funnel });
  const pipelineQ = useQuery({ queryKey: ["pipeline"], queryFn: api.pipeline });
  const sourcesQ = useQuery({ queryKey: ["sources"], queryFn: api.sources });
  const trendQ = useQuery({ queryKey: ["trend"], queryFn: () => api.trend(7) });
  const meQ = useQuery({ queryKey: ["me"], queryFn: api.me });
  const isAdmin = meQ.data?.role === "admin";
  const managersQ = useQuery({
    queryKey: ["managers"],
    queryFn: api.managers,
    enabled: isAdmin,
  });

  const funnelData = (funnelQ.data || []).map((p) => ({
    name: STAGE_LABEL[p.stage] || p.stage,
    count: p.count,
    conv: p.conversion_from_prev ? Math.round(p.conversion_from_prev * 100) : null,
    stage: p.stage,
  }));

  const pipelineRevenue = (pipelineQ.data || []).reduce((s, p) => s + p.amount, 0);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Аналитика</h2>
        <div style={{ color: "var(--muted)" }}>Pipeline: <b>{pipelineRevenue.toLocaleString("ru-RU")}</b></div>
      </div>

      <div className="analytics-grid">
        <div className="panel">
          <h3>Воронка: количество</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={funnelData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a3142" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8c95ad" }} />
              <YAxis tick={{ fontSize: 11, fill: "#8c95ad" }} />
              <Tooltip contentStyle={{ background: "#161a23", border: "1px solid #2a3142" }} />
              <Bar dataKey="count">
                {funnelData.map((d) => (
                  <Cell key={d.stage} fill={STAGE_COLOR[d.stage] || "#5b8def"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="panel">
          <h3>Pipeline по статусам, сумма</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={(pipelineQ.data || []).map((p) => ({
              name: STAGE_LABEL[p.stage] || p.stage,
              amount: p.amount,
              stage: p.stage,
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a3142" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8c95ad" }} />
              <YAxis tick={{ fontSize: 11, fill: "#8c95ad" }} />
              <Tooltip contentStyle={{ background: "#161a23", border: "1px solid #2a3142" }} />
              <Bar dataKey="amount">
                {(pipelineQ.data || []).map((p) => (
                  <Cell key={p.stage} fill={STAGE_COLOR[p.stage] || "#5b8def"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="panel">
          <h3>Динамика за неделю</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={(trendQ.data?.days || []).map((d) => ({
              day: d.day.slice(5),
              новых: d.new_leads,
              оплачено: d.paid,
              выручка: d.revenue,
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a3142" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#8c95ad" }} />
              <YAxis tick={{ fontSize: 11, fill: "#8c95ad" }} />
              <Tooltip contentStyle={{ background: "#161a23", border: "1px solid #2a3142" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="новых" stroke="#5b8def" />
              <Line type="monotone" dataKey="оплачено" stroke="#6dd6a6" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="panel">
          <h3>Источники</h3>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ color: "var(--muted)", textAlign: "left" }}>
                <th>Источник</th><th>Всего</th><th>Won</th><th>Выручка</th><th>Win %</th>
              </tr>
            </thead>
            <tbody>
              {(sourcesQ.data || []).map((s) => (
                <tr key={s.source} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "6px 0" }}>{SOURCE_LABEL[s.source] || s.source}</td>
                  <td>{s.leads_total}</td>
                  <td>{s.leads_won}</td>
                  <td>{s.revenue_won.toLocaleString("ru-RU")}</td>
                  <td>{(s.win_rate * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isAdmin && (
          <div className="panel" style={{ gridColumn: "1 / -1" }}>
            <h3>Активность менеджеров</h3>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ color: "var(--muted)", textAlign: "left" }}>
                  <th>Менеджер</th>
                  <th>Активных</th>
                  <th>Заметок (30д)</th>
                  <th>Задач (откр.)</th>
                  <th>Закрыто задач (30д)</th>
                  <th>Сделок won</th>
                  <th>Выручка</th>
                </tr>
              </thead>
              <tbody>
                {(managersQ.data || []).map((m) => (
                  <tr key={m.user_id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={{ padding: "6px 0" }}>{m.full_name || `@${m.tg_username || m.user_id}`}</td>
                    <td>{m.leads_active}</td>
                    <td>{m.notes_count}</td>
                    <td>{m.tasks_open}</td>
                    <td>{m.tasks_done}</td>
                    <td>{m.deals_won}</td>
                    <td>{m.revenue_won.toLocaleString("ru-RU")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 24, color: "var(--muted)", fontSize: 12 }}>
        Продукты: {Object.values(PRODUCT_LABEL).join(", ")}
      </div>
    </>
  );
}
